import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Drawer, Grid, IconButton, Paper, Typography } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { api, downloadPdf, setStaffTokenRenewer, staffStartStore, staffStore, tokenStore } from "./api";
import { ErrorBanner, Field, GhostButton, OutlineButton, PreviewLock, PrimaryButton, SignupStepContext, StepCard } from "./formParts";
import { Icon, STEP_CAPTIONS, T, cssVars, signupFrameTheme, signupTheme, useSignupFonts } from "./signupDesign";
import { BankStep, BusinessStep, LegalStep, OwnersStep, ProcessingStep } from "./ApplicationSteps";
import DocumentsStep from "./DocumentsStep";
import TermsStep from "./TermsStep";
import SignStep from "./SignStep";
import { StaffBanner, StaffFeesStep, StaffSendStep } from "./StaffPanels";
import PartnerHeader, { PARTNER_HEADER_HEIGHT } from "./PartnerHeader";
import Seo from "../../components/Seo/Seo";

/**
 * Restaurant sign-up: the merchant application that collects everything
 * our payment processors need to open the restaurant's payment accounts, its
 * KYB documents (uploadable from a phone via QR code), and the signed Master
 * Merchant Services Agreement (v3). Progress is saved on our server after every step; the link
 * in the applicant's email brings them back to it.
 *
 * Staff mode: a super admin or an agent opens this from the admin panel
 * (?staffStart= to start one, ?staff= for an existing one) and fills in the
 * same steps; Fees & terms sets the restaurant's fees, and the last step
 * sends it to the restaurant to review and sign (StaffPanels.js).
 */

const STEPS = [
  ["business", "Basic info"],
  ["legal", "Legal entity"],
  ["processing", "Card processing"],
  ["owners", "Owners"],
  ["bank", "Payout bank"],
  ["documents", "Documents"],
  ["terms", "Fees & terms"],
  ["sign", "Sign"],
];
const STEP_KEYS = STEPS.map(([k]) => k);

function firstOpenStep(view) {
  if (!view) return "business";
  if (view.status !== "DRAFT") return "done";
  const missing = view.missing || [];
  return missing.length ? missing[0].step : "sign";
}

// Steps that are a form of fields ("All fields are required unless marked optional.").
const FORM_STEPS = new Set(["business", "legal", "processing", "owners", "bank"]);

// The header (PartnerHeader) is fixed; the page starts under it.
const HEADER_OFFSET = { xs: "88px", md: "104px" };

const SERVICES = ["Web Design", "Delivery", "Online Ordering", "Google Marketing", "Social Media Marketing", "Email Marketing"];

const srOnly = { position: "absolute", width: "1px", height: "1px", p: 0, m: "-1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

function Eyebrow({ children }) {
  return (
    <Typography sx={{ color: T.redText, fontSize: 13, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", mb: 1 }}>
      {children}
    </Typography>
  );
}

/** The full hero, on step 1 before an application is started. */
function Hero() {
  return (
    <Box component="section" sx={{ background: T.heroBg, borderBottom: `1px solid ${T.heroBorder}`, pt: HEADER_OFFSET, pb: { xs: 4, md: 6 } }}>
      {/* "What you'll need" is on step 2 once started (WhatYouNeed.js), not here. */}
      <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 2, sm: 4 } }}>
        <Box sx={{ maxWidth: 860 }}>
          <Eyebrow>Become a partner restaurant</Eyebrow>
          <Typography component="h1" sx={{ fontFamily: T.display, fontWeight: 700, letterSpacing: "-0.02em", fontSize: { xs: 36, sm: 48, md: 60 }, lineHeight: 1.05, color: T.text, mb: 2.5 }}>
            New Restaurant Signup
          </Typography>
          <Typography sx={{ fontSize: { xs: 17, sm: 21 }, fontWeight: 600, color: T.text, lineHeight: 1.4, mb: 1.5 }}>
            RushMyFood offers restaurants real one-on-one marketing for their online ordering.
          </Typography>
          <Typography sx={{ fontSize: { xs: 16, sm: 17 }, color: T.text2, lineHeight: 1.6, maxWidth: 680, mb: 3 }}>
            We utilize Google, Facebook, Instagram and Email Marketing to generate orders for your
            restaurant — at a much lower rate than the competition. In fact, it could be as low
            as $0. Learn more by filling out the form below.
          </Typography>
          <Box component="ul" aria-label="Services" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {SERVICES.map((name) => (
              <Box
                component="li"
                key={name}
                sx={{ background: "#fff", border: `1px solid ${T.tagBorder}`, color: T.linkHover, borderRadius: 999, px: { xs: 1.25, sm: 1.75 }, py: { xs: 0.5, sm: 0.75 }, fontSize: { xs: 13, sm: 14 }, fontWeight: 600 }}
              >
                {name}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Steps 2–8 (and after starting): the eyebrow and title in a slim band. */
function SlimHero({ savedStep }) {
  return (
    <Box component="section" sx={{ background: T.heroBg, borderBottom: `1px solid ${T.heroBorder}`, pt: HEADER_OFFSET, pb: { xs: 2.5, md: 3.5 } }}>
      <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: 2, sm: 4 }, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end", justifyContent: "space-between" }}>
        <Box>
          <Eyebrow>Become a partner restaurant</Eyebrow>
          <Typography component="h1" sx={{ fontFamily: T.display, fontWeight: 700, letterSpacing: "-0.02em", fontSize: { xs: 26, sm: 30 }, lineHeight: 1.15, color: T.text }}>
            New Restaurant Signup
          </Typography>
        </Box>
        {savedStep && (
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, background: "#fff", border: "1px solid #CFE3CF", color: T.green, borderRadius: 999, px: 1.5, py: 0.5, fontSize: 14, fontWeight: 600 }}>
            <Icon.Check size={16} />
            Step {savedStep} saved
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** The numbered list of steps; shared by the sidebar and the phone sheet. */
function StepList({ steps, onGo }) {
  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 1 }}>
      {steps.map((st) => {
        // A finished step keeps its tick when it is the one open; the open
        // one is marked by its row instead.
        const circle = st.done
          ? { background: T.green, color: "#fff", border: `1.5px solid ${T.green}` }
          : st.current
            ? { background: T.red, color: "#fff", border: `1.5px solid ${T.red}` }
            : { background: "#fff", color: T.muted, border: `1.5px solid ${T.circle}` };
        return (
          <li key={st.key}>
            <Box
              component="button"
              type="button"
              onClick={() => onGo(st.key)}
              aria-current={st.current ? "step" : undefined}
              sx={{
                width: "100%", minHeight: 56, display: "flex", alignItems: "center", gap: 1.5, px: 1.5, py: 1, border: 0, borderRadius: "14px",
                background: st.current ? T.redTint : "transparent", cursor: "pointer", textAlign: "left", font: "inherit", color: "inherit",
                "&:hover": { background: st.current ? T.redTint : T.fill },
                "&:focus-visible": { outline: "none", boxShadow: `inset 0 0 0 2px ${T.red}` },
              }}
            >
              <Box aria-hidden="true" sx={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0, ...circle }}>
                {st.done ? <Icon.Check size={16} /> : st.number}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box component="span" sx={{ display: "block", fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: st.current ? T.redTextStrong : T.text }}>
                  <Box component="span" sx={srOnly}>Step {st.number}: </Box>
                  {st.label}
                  {st.done && <Box component="span" sx={srOnly}> (completed)</Box>}
                </Box>
                <Box component="span" sx={{ display: "block", fontSize: 13, color: T.muted, lineHeight: 1.35, mt: 0.25 }}>{st.caption}</Box>
              </Box>
            </Box>
          </li>
        );
      })}
    </Box>
  );
}

/** The sidebar: progress, the steps, and the save note. */
function StepNavigator({ steps, number }) {
  const allDone = steps.list.every((st) => st.done);
  return (
    <Box component="nav" aria-label="Application steps" sx={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text }}>Your application</Typography>
          <Typography sx={{ color: T.muted, fontSize: 14 }}>Step {number} of {steps.list.length}</Typography>
        </Box>
        <Box
          role="progressbar"
          aria-label="Progress"
          aria-valuemin={1}
          aria-valuemax={steps.list.length}
          aria-valuenow={number}
          sx={{ height: 6, borderRadius: 999, background: T.track, mt: 1.5, overflow: "hidden" }}
        >
          <Box sx={{ height: "100%", width: allDone ? "100%" : `${(number / steps.list.length) * 100}%`, background: allDone ? T.green : T.red, borderRadius: 999 }} />
        </Box>
      </Box>
      <StepList steps={steps.list} onGo={steps.onGo} />
      <Box sx={{ borderTop: `1px solid ${T.divider}`, px: 2.5, py: 1.75, display: "flex", alignItems: "center", gap: 1, color: T.muted, fontSize: 14 }}>
        <Box sx={{ color: T.green, display: "flex" }}><Icon.CloudCheck size={20} /></Box>
        Saved automatically as you go
      </Box>
    </Box>
  );
}

/** Phones: a sticky bar with the step, segments, and the full list in a sheet. */
function MobileProgress({ steps, number, label, frame }) {
  const [open, setOpen] = useState(false);
  const total = steps.list.length;
  return (
    <>
      <Box
        sx={{
          // In the admin's frame it is the only step navigation, at any width.
          display: frame ? "block" : { xs: "block", sm: "none" }, position: "sticky", top: frame ? 0 : PARTNER_HEADER_HEIGHT, zIndex: 5,
          mx: frame ? { xs: -2, sm: -3 } : -2, px: frame ? { xs: 2, sm: 3 } : 2, py: 1.25, mb: 2, background: T.card, borderBottom: `1px solid ${T.cardBorder}`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: T.muted, fontSize: 13 }}>Step {number} of {total}</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 16, color: T.text, lineHeight: 1.3 }}>{label}</Typography>
          </Box>
          <GhostButton onClick={() => setOpen(true)} aria-haspopup="dialog" startIcon={<Icon.List size={18} />} sx={{ px: 1.75, flexShrink: 0 }}>
            All steps
          </GhostButton>
        </Box>
        <Box aria-hidden="true" sx={{ display: "grid", gridTemplateColumns: `repeat(${total}, 1fr)`, gap: "4px", mt: 1.25 }}>
          {steps.list.map((st) => (
            <Box key={st.key} sx={{ height: 6, borderRadius: 999, background: st.done ? T.green : st.current ? T.red : T.track }} />
          ))}
        </Box>
      </Box>
      <Drawer anchor="bottom" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: "20px 20px 0 0", maxHeight: "85vh" } }}>
        <Box component="nav" aria-label="Application steps" sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2.5, pt: 2, pb: 0.5 }}>
            <Typography component="h2" sx={{ fontWeight: 700, fontSize: 18, color: T.text }}>All steps</Typography>
            <IconButton aria-label="Close" onClick={() => setOpen(false)} sx={{ width: 44, height: 44, color: T.muted }}>
              <Box sx={{ display: "flex", transform: "rotate(45deg)" }}><Icon.Plus size={22} /></Box>
            </IconButton>
          </Box>
          <StepList steps={steps.list} onGo={(key) => { setOpen(false); steps.onGo(key); }} />
        </Box>
      </Drawer>
    </>
  );
}

/**
 * Reopened after the owner signed (backend agreementChange): what changed
 * since, and that it needs their signature again.
 */
function AgreementChanged({ change, staffMode }) {
  const list = (names) => (names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0]);
  const what = [
    change.added && change.added.length ? `${staffMode ? "" : "we "}added ${list(change.added)}` : null,
    change.removed && change.removed.length ? `${staffMode ? "" : "we "}took out ${list(change.removed)}` : null,
  ].filter(Boolean).join(" and ");
  return (
    <Box role="status" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, background: T.infoBg, border: `1px solid ${T.infoBorder}`, color: T.infoText, borderRadius: "16px", px: 2, py: 1.5, mb: 2.5 }}>
      <Box sx={{ display: "flex", mt: "2px" }}><Icon.Info size={18} /></Box>
      <Typography sx={{ fontSize: 15, color: T.infoText, lineHeight: 1.5 }}>
        {staffMode
          ? `Reopened for signing${what ? `: ${what} since the owner signed` : ""}. Send it for signature on the last step; the owner is told what changed.`
          : `Your agreement was updated${what ? `: ${what}` : ""}. Because it changed, please review it and sign again on the last step. Everything you entered before is still here.`}
      </Typography>
    </Box>
  );
}

/** "You're looking ahead": before starting, on any step but the first. */
function LookingAhead({ staffMode, onGo }) {
  return (
    <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", background: T.infoBg, border: `1px solid ${T.infoBorder}`, color: T.infoText, borderRadius: "16px", px: 2, py: 1.75, mb: 2.5 }}>
      <Box sx={{ display: "flex", alignSelf: "flex-start", mt: "2px" }}><Icon.Info size={20} /></Box>
      <Typography sx={{ flex: "1 1 240px", fontSize: 15, color: T.infoText, lineHeight: 1.5 }}>
        {staffMode
          ? "Create the application on step 1 first; then every step can be filled in."
          : <><strong>You're looking ahead.</strong> Start with step 1 to fill in your application; it's saved as you go.</>}
      </Typography>
      <Button
        onClick={onGo}
        endIcon={<Icon.ArrowRight size={16} />}
        sx={{ background: "#fff", color: T.infoText, border: `1px solid ${T.infoBorder}`, px: 2, flexShrink: 0, "&:hover": { background: "#fff", borderColor: T.infoText } }}
      >
        Go to step 1
      </Button>
    </Box>
  );
}

// An agent's link (partner-signup?agent=CODE) is remembered on this device
// until the application is started, so the agent is credited even if the
// restaurant browses away and comes back.
const AGENT_KEY = "rmf-agent-code";
const agentStore = {
  get() {
    try { return localStorage.getItem(AGENT_KEY); } catch (e) { return null; }
  },
  set(code) {
    try { localStorage.setItem(AGENT_KEY, code); } catch (e) { /* private mode */ }
  },
  clear() {
    try { localStorage.removeItem(AGENT_KEY); } catch (e) { /* nothing stored */ }
  },
};

/** "Already started?" — emails a fresh link to an open application. */
function ResumeLinkCard() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const sendLink = async () => {
    setMsg(null);
    try {
      const res = await api("POST", "/resume-link", { body: { email } });
      setMsg({ severity: "success", text: res.message });
    } catch (e) {
      setMsg({ severity: "error", text: e.message });
    }
  };
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${T.cardBorder}`, borderRadius: "20px", p: "var(--rmf-card-pad, 20px)", mt: 3 }}>
      <Typography component="h2" sx={{ fontWeight: 700, fontSize: 17, color: T.text, mb: 1.5 }}>Already started?</Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Box sx={{ flex: "1 1 240px" }}>
          <Grid container><Field label="Email you applied with" name="resumeEmail" type="email" value={email} onChange={setEmail} required inputProps={{ autoComplete: "email" }} /></Grid>
        </Box>
        <OutlineButton onClick={sendLink} disabled={!email} sx={{ minHeight: 48 }}>
          Email me my link
        </OutlineButton>
      </Box>
      {msg && <Alert severity={msg.severity} sx={{ mt: 2 }}>{msg.text}</Alert>}
    </Paper>
  );
}

function DoneScreen({ view, token, payoutsDone }) {
  if (view.staff) {
    return (
      <StepCard title="Signed by the restaurant">
        <Typography sx={{ color: T.text2, fontSize: 16, lineHeight: 1.7 }}>
          {view.application.business.dbaName} signed the agreement{view.signedAt ? ` on ${new Date(view.signedAt).toLocaleString()}` : ""}. Review and approve it on the Partner Signups page of the admin panel.
        </Typography>
      </StepCard>
    );
  }
  return <MerchantDoneScreen view={view} token={token} payoutsDone={payoutsDone} />;
}

function MerchantDoneScreen({ view, token, payoutsDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const download = async () => {
    setBusy(true);
    try {
      await downloadPdf("/agreement/signed", token, "RushMyFood-Merchant-Agreement-signed.pdf");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <StepCard title={payoutsDone ? "Payout details submitted" : "Thank you, your agreement is signed"}>
      <ErrorBanner error={error} />
      <Typography sx={{ color: T.text2, fontSize: 16, lineHeight: 1.7, mb: 3 }}>
        {payoutsDone
          ? "Thanks for confirming your payout details. If our payments partner needs anything else they will email you; otherwise you're all set to get paid."
          : view.status === "ONBOARDED"
            ? "You're approved. Check your email for your dashboard login and a link to confirm your payout details with our payments partner."
            : "We've emailed you a signed copy. Our team will review your application and documents, usually within 1 to 2 business days, and email you once you're approved."}
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        <PrimaryButton onClick={download} busy={busy} arrow={false}>
          <Icon.FileText size={18} />
          Download my signed agreement
        </PrimaryButton>
      </Box>
      <Typography sx={{ color: T.hint, fontSize: 14, mt: 3 }}>
        Questions? Email <Box component="a" href="mailto:info@rushmyfood.com" sx={{ color: T.link, fontWeight: 600, "&:hover": { color: T.linkHover } }}>info@rushmyfood.com</Box>.
      </Typography>
    </StepCard>
  );
}

function PartnerSignup() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const linkToken = params.get("resume");
  const agentParam = params.get("agent");
  const staffParam = params.get("staff");
  const staffStartParam = params.get("staffStart");
  const payoutsDone = params.get("payouts") === "done";
  // Inside the admin's application page (an iframe, ?frame=1): only the form,
  // with no site header, hero or footer. Kept in state because the URL's
  // parameters are dropped once read.
  const [frame] = useState(() => params.get("frame") === "1");
  // Bumped when the admin page changes the application (its fees): the open
  // step remounts with the new answers.
  const [refreshKey, setRefreshKey] = useState(0);
  useSignupFonts();

  // A link in the URL wins; otherwise this tab's staff session, then the
  // merchant's saved application. In the admin's frame only the URL counts:
  // every frame of this site in the admin tab shares its session storage, so
  // a stored token could be another application's.
  const [token, setToken] = useState(() => (frame
    ? staffParam || null
    : staffParam || (staffStartParam ? null : linkToken || staffStore.get() || tokenStore.get())));
  // Staff starting a new application: the token to create it with.
  const [staffStart, setStaffStart] = useState(() => (frame
    ? staffStartParam || null
    : staffStartParam || (staffParam || linkToken ? null : staffStartStore.get())));
  const [meta, setMeta] = useState(null);
  const [view, setView] = useState(null);
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(!!token);
  const [loadError, setLoadError] = useState(null);
  // Staff starting one: who they are (name and role), before the application
  // exists to say so.
  const [starter, setStarter] = useState(null);
  // Starting from step 1 hands us the new application directly; reloading it
  // would remount step 1 halfway through its save.
  const skipLoad = useRef(false);

  // The admin page pointed this frame at another application, or at a new
  // one: follow the URL, never keep the application that was open (a frame
  // can change its link without reloading).
  useEffect(() => {
    if (!frame) return;
    if (staffParam && staffParam !== token) {
      setStaffStart(null);
      setView(null);
      setStep(null);
      setLoadError(null);
      setToken(staffParam);
    } else if (staffStartParam && staffStartParam !== staffStart) {
      setToken(null);
      setView(null);
      setStep(null);
      setLoadError(null);
      setStaffStart(staffStartParam);
    }
  }, [frame, staffParam, staffStartParam, token, staffStart]);

  // Keep the token from the email link (or an agent's code) on this device,
  // then drop it from the URL. Not in the admin's frame: its token stays in
  // the frame alone.
  useEffect(() => {
    if (staffParam && !frame) {
      staffStore.set(staffParam);
      staffStartStore.clear();
    }
    if (staffStartParam && !frame) {
      staffStartStore.set(staffStartParam);
      staffStore.clear();
    }
    if (linkToken) {
      tokenStore.set(linkToken);
      staffStore.clear();
      staffStartStore.clear();
    }
    if (agentParam) agentStore.set(agentParam);
    if (linkToken || agentParam || staffParam || staffStartParam) navigate("/partner-signup", { replace: true });
  }, [linkToken, agentParam, staffParam, staffStartParam, navigate, frame]);

  useEffect(() => {
    api("GET", "/meta").then(setMeta).catch((e) => setLoadError(e.message));
  }, []);

  useEffect(() => {
    if (!staffStart) return undefined;
    let alive = true;
    api("POST", "/staff/me", { body: { startToken: staffStart } }).then((w) => alive && setStarter(w)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [staffStart]);
  // Signing refuses fees that changed since the page loaded; this fetches the
  // new ones: the standard (meta) and this restaurant's own (the application).
  const reloadMeta = () => Promise.all([
    api("GET", "/meta").then(setMeta).catch(() => {}),
    token ? api("GET", "/application", { token }).then(setView).catch(() => {}) : null,
  ]);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const v = await api("GET", "/application", { token: t });
      setView(v);
      setStep(firstOpenStep(v));
      setLoadError(null);
    } catch (e) {
      if (e.status === 401) {
        // Whichever was in use: a staff token is a JWT (it has dots).
        if (String(t).includes(".")) staffStore.clear();
        else tokenStore.clear();
        setToken(null);
        setLoadError(e.message);
      } else setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    if (skipLoad.current) {
      skipLoad.current = false;
      return;
    }
    load(token);
  }, [token, load]);

  // The admin page around the frame refreshes its panel when the form saves,
  // and asks the form to reload when it changes the application itself.
  const tellAdmin = (type, v) => {
    if (!frame || window.parent === window) return;
    window.parent.postMessage({ source: "rmf-signup", type, id: v && v.id, status: v && v.status }, "*");
  };
  useEffect(() => {
    if (!frame || !token) return undefined;
    const onMessage = (e) => {
      if (!e.data || e.data.source !== "rmf-admin" || e.data.type !== "refresh") return;
      Promise.all([
        api("GET", "/meta").then(setMeta),
        api("GET", "/application", { token }).then(setView),
      ]).then(() => setRefreshKey((k) => k + 1)).catch(() => {});
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [frame, token]);

  // In the admin's frame: when the staff link expires, ask the admin page for
  // a fresh one; api.js sends the refused request again with it. The open
  // step stays as it is (skipLoad: no reload for the new token).
  useEffect(() => {
    if (!frame || window.parent === window) return undefined;
    setStaffTokenRenewer(() => new Promise((resolve) => {
      let timer = null;
      const onMessage = (e) => {
        if (e.source !== window.parent) return;
        const m = e.data || {};
        if (m.source !== "rmf-admin" || m.type !== "token" || !m.token) return;
        finish(m.token);
      };
      function finish(fresh) {
        window.removeEventListener("message", onMessage);
        clearTimeout(timer);
        if (fresh) {
          skipLoad.current = true;
          setToken(fresh);
        }
        resolve(fresh || null);
      }
      timer = setTimeout(() => finish(null), 15000);
      window.addEventListener("message", onMessage);
      window.parent.postMessage({ source: "rmf-signup", type: "renew" }, "*");
    }));
    return () => setStaffTokenRenewer(null);
  }, [frame]);

  // The open step's answers not saved yet: saved before moving to another
  // step (the step list, Back), so "Saved automatically as you go" holds.
  // Each form step registers how to save itself while it has changes.
  const draftRef = useRef(null);
  const registerDraft = useCallback((fn) => {
    draftRef.current = fn;
  }, []);

  const go = async (key, { saved } = {}) => {
    const draft = draftRef.current;
    draftRef.current = null;
    if (draft && !saved) {
      try {
        await draft();
      } catch (e) {
        // Kept as typed on the server where it could be; Save & continue shows what to fix.
      }
    }
    setStep(key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // Before starting, every step can be opened and read; the blank
  // application comes from the server (meta.preview).
  const preview = !token || !view;
  const activeView = preview ? meta && meta.preview : view;
  // In the admin's frame a signed (or rejected) application is shown in the
  // same steps, locked, instead of the thank-you screen.
  const locked = frame && !preview && view.status !== "DRAFT";
  const current = !step || (locked && step === "done") ? "business" : step;
  const idx = STEP_KEYS.indexOf(current);
  // Save & continue already saved the step.
  const next = () => go(STEP_KEYS[idx + 1] || "sign", { saved: true });
  const back = () => go(STEP_KEYS[idx - 1] || "business");
  const onSaved = (v) => {
    setView(v);
    if (v.status && v.status !== "DRAFT" && step === "sign") setStep("done");
    tellAdmin("saved", v);
  };
  // Step 1's "Start my application": creates the application, emails the
  // link, and switches the wizard out of preview without a reload. Staff
  // create it with their start token instead, and nothing is emailed.
  const onStart = async (fields) => {
    if (staffStart) {
      const created = await api("POST", "/staff/start", { body: { ...fields, startToken: staffStart } });
      if (!frame) {
        staffStore.set(created.token);
        staffStartStore.clear();
      }
      setStaffStart(null);
      skipLoad.current = true;
      setToken(created.token);
      setView(created);
      setLoadError(null);
      tellAdmin("created", created);
      return created;
    }
    const res = await api("POST", "/start", { body: { ...fields, agentCode: agentStore.get() || undefined } });
    agentStore.clear();
    skipLoad.current = true;
    tokenStore.set(res.token);
    setToken(res.token);
    setView(res);
    setLoadError(null);
    return res;
  };
  const startOver = () => {
    tokenStore.clear();
    setToken(null);
    setView(null);
    setStep(null);
  };

  const missingSteps = new Set(((view && view.missing) || []).map((m) => m.step));
  // The last step (Sign, or Send for staff) is done once the restaurant signed.
  const signed = !!(view && (view.signedAt || ["SIGNED", "ONBOARDED"].includes(view.status)));
  // Staff (from the admin panel): starting one, or on one they opened.
  const staff = !preview && view && view.staff ? view.staff : null;
  const staffMode = !!staff || !!staffStart;
  const stepList = staffMode ? STEPS.map(([k, l]) => [k, k === "sign" ? "Send" : l]) : STEPS;
  const stepProps = { view: activeView, meta, token, preview, onStart, onSaved, onNext: next, onBack: idx > 0 ? back : undefined, staffStarting: !!staffStart, registerDraft: preview ? undefined : registerDraft };

  let body;
  let wizard = false;
  if (payoutsDone && !token) {
    // Back from Stripe on a device that never opened the application.
    body = (
      <StepCard title="Payout details submitted">
        <Typography sx={{ color: T.text2, fontSize: 16, lineHeight: 1.7 }}>
          Thanks for confirming your payout details. If our payments partner needs anything else they will email you; otherwise you're all set to get paid.
        </Typography>
      </StepCard>
    );
  } else if (!meta || loading) {
    body = loadError ? <Alert severity="error" icon={<Icon.Alert size={20} />}>{loadError}</Alert> : <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress sx={{ color: T.red }} /></Box>;
  } else if (!preview && !locked && (step === "done" || view.status !== "DRAFT")) {
    body = <DoneScreen view={view} token={token} payoutsDone={payoutsDone} />;
  } else if (!activeView) {
    body = <Alert severity="error" icon={<Icon.Alert size={20} />}>The sign-up form couldn't load. Please refresh the page.</Alert>;
  } else {
    const steps = {
      business: <BusinessStep {...stepProps} />,
      legal: <LegalStep {...stepProps} />,
      processing: <ProcessingStep {...stepProps} />,
      owners: <OwnersStep {...stepProps} />,
      bank: <BankStep {...stepProps} />,
      documents: <DocumentsStep {...stepProps} />,
      terms: staff ? <StaffFeesStep {...stepProps} /> : <TermsStep {...stepProps} />,
      sign: staff
        ? <StaffSendStep {...stepProps} onGoTo={go} />
        : <SignStep {...stepProps} onGoTo={go} reloadMeta={reloadMeta} />,
    };
    const number = Math.max(idx, 0) + 1;
    const nav = {
      onGo: go,
      list: stepList.map(([key, label], i) => ({
        key,
        label,
        number: i + 1,
        caption: STEP_CAPTIONS[key],
        current: key === current,
        done: !preview && (key === "sign" ? signed : !missingSteps.has(key)),
      })),
    };
    const stepContext = { number, total: stepList.length, name: (stepList[idx] || stepList[0])[1], formStep: FORM_STEPS.has(current) };
    wizard = true;
    body = (
      <>
        {staffMode && !frame && <StaffBanner staff={staff || starter} starting={!!staffStart} />}
        {preview && loadError && <Alert severity="warning" icon={<Icon.Alert size={20} />} sx={{ mb: 3 }}>{loadError}</Alert>}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "flex-start" }}>
          <Box
            sx={{
              flex: "1 1 270px", display: frame ? "none" : { xs: "none", sm: "block" },
              "@media (min-width: 1000px) and (min-height: 820px)": { position: "sticky", top: PARTNER_HEADER_HEIGHT + 24 },
            }}
          >
            <StepNavigator steps={nav} number={number} />
          </Box>
          <Box sx={{ flex: "999 1 560px", minWidth: 0 }}>
            <MobileProgress steps={nav} number={number} label={stepContext.name} frame={frame} />
            {preview && current !== "business" && <LookingAhead staffMode={staffMode} onGo={() => go("business")} />}
            {locked && (
              <Box role="status" sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, background: T.infoBg, border: `1px solid ${T.infoBorder}`, color: T.infoText, borderRadius: "16px", px: 2, py: 1.5, mb: 2.5 }}>
                <Box sx={{ display: "flex", mt: "2px" }}><Icon.Lock size={18} /></Box>
                <Typography sx={{ fontSize: 15, color: T.infoText, lineHeight: 1.5 }}>
                  {view.status === "REJECTED"
                    ? "This application was rejected, so it is read-only."
                    : `Signed by the restaurant${view.signedAt ? ` on ${new Date(view.signedAt).toLocaleDateString()}` : ""}, so it is read-only.`}
                </Typography>
              </Box>
            )}
            {!preview && !locked && view.change && <AgreementChanged change={view.change} staffMode={staffMode} />}
            {/* key: each step mounts fresh with the latest saved answers */}
            <SignupStepContext.Provider value={stepContext}>
              <PreviewLock on={locked}>
                <Box key={`${current}-${refreshKey}`}>{steps[current]}</Box>
              </PreviewLock>
            </SignupStepContext.Provider>
            {frame ? null : staffMode ? (
              !preview && (
                <Typography sx={{ color: T.hint, fontSize: 14, textAlign: "center", mt: 3 }}>
                  Filling in for {view.application.business.dbaName}. Saved as you go.
                </Typography>
              )
            ) : preview ? (
              <ResumeLinkCard />
            ) : (
              <Typography sx={{ color: T.hint, fontSize: 14, textAlign: "center", mt: 3 }}>
                Applying for {view.application.business.dbaName}. Your progress is saved.{" "}
                <Box component="button" type="button" onClick={startOver} sx={{ border: 0, background: "none", color: T.link, textDecoration: "underline", cursor: "pointer", p: 0, font: "inherit", "&:hover": { color: T.linkHover } }}>
                  Not you? Start a new application
                </Box>
              </Typography>
            )}
          </Box>
        </Box>
      </>
    );
  }

  // The full hero only on step 1 before starting; a slim band everywhere else.
  const fullHero = !token && !staffStart && !payoutsDone && current === "business";
  const prevKey = idx > 0 ? STEP_KEYS[idx - 1] : null;
  const savedStep = wizard && !preview && prevKey && !missingSteps.has(prevKey) ? idx : null;

  if (frame) {
    return (
      <Box sx={{ background: T.pageBg, minHeight: "100vh" }}>
        <ThemeProvider theme={signupFrameTheme}>
          <Box sx={{ ...cssVars, "--rmf-card-pad": "20px", "@media (min-width: 600px)": { "--rmf-card-pad": "28px" }, fontFamily: T.body, color: T.text, fontSize: 16, lineHeight: 1.5 }}>
            <Box sx={{ maxWidth: 980, mx: "auto", px: { xs: 2, sm: 3 }, pt: wizard ? 0 : 3, pb: 4 }}>
              {body}
            </Box>
          </Box>
        </ThemeProvider>
      </Box>
    );
  }

  return (
    <Box sx={{ background: T.pageBg }}>
      {/* Without this the page inherited the homepage's canonical, so Google
          folded it into "/" and never listed it on its own. */}
      <Seo
        title="Become a Partner Restaurant"
        description="Sign up your restaurant with RushMyFood for real one-on-one marketing on Google, Facebook, Instagram and email, at a much lower rate than the competition."
        path="/partner-signup"
      />
      <ThemeProvider theme={signupTheme}>
        <PartnerHeader staff={staffMode ? staff || starter : null} />
        <Box sx={{ ...cssVars, fontFamily: T.body, color: T.text, fontSize: 16, lineHeight: 1.5 }}>
          {fullHero ? <Hero /> : <SlimHero savedStep={savedStep} />}
          <Box sx={{ maxWidth: wizard ? 1240 : 820, mx: "auto", px: { xs: 2, sm: 4 }, pt: { xs: 2.5, sm: 4 }, pb: { xs: 4, sm: 8 } }}>
            {body}
          </Box>
        </Box>
      </ThemeProvider>
    </Box>
  );
}

export default PartnerSignup;
